// Email possession verification, in place (§2).
//
// Progressive enhancement over two real forms. With this pack disabled both
// still post and the server still redirects, which is why the code panel is
// rendered visible and hidden HERE on boot rather than in the markup — the
// no-JavaScript path must not be left with a panel it cannot open.
//
// What the member gets with it enabled: one button, an answer written into the
// page where they are looking, and only then a box for the code. No page
// reload, and no flash claiming an email that was never sent.
//
// The server remains the authority on everything that matters. The send button
// is disabled while a request is in flight to stop a double click, but the
// resend limit is enforced in ProxyVerification.issue, not here.

function csrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.content : '';
}

function initProxyVerification(root) {
  if (root.dataset.proxyVerificationReady === 'true') return;
  root.dataset.proxyVerificationReady = 'true';

  const sendForm = root.querySelector('[data-verification-send-form]');
  const codeForm = root.querySelector('[data-verification-code-form]');
  const sendButton = root.querySelector('[data-verification-send]');
  const status = root.querySelector('[data-verification-status]');
  const panel = root.querySelector('[data-verification-code-panel]');
  const codeInput = panel ? panel.querySelector('input[name="code"]') : null;

  if (!sendForm || !codeForm || !panel) return;

  // The one thing this pack does before the member touches anything.
  panel.hidden = true;

  const say = (message) => {
    if (status) status.textContent = message || '';
  };

  const revealCode = () => {
    panel.hidden = false;
    if (codeInput) codeInput.focus();
  };

  const post = async (url, method, body) => {
    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': csrfToken(),
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });

    // A 429 or a 422 is an answer, not a failure — both carry a message the
    // member needs to read. Only a response with no JSON body at all is
    // treated as a breakdown.
    let payload = null;
    try {
      payload = await response.json();
    } catch (error) {
      payload = null;
    }

    return payload;
  };

  sendForm.addEventListener('submit', async (event) => {
    event.preventDefault();

    if (sendButton) sendButton.disabled = true;
    say('Sending…');

    try {
      const data = await post(
        root.dataset.sendUrl,
        'POST',
        {
          next: sendForm.querySelector('input[name="next"]')?.value,
          purpose: sendForm.querySelector('input[name="purpose"]')?.value,
        },
      );

      if (!data) {
        say('We could not send the code just then. Please try again.');
        return;
      }

      say(data.message);

      // Throttled reveals the panel too: a code went out a moment ago and it
      // is still usable, so the member needs somewhere to type it.
      if (data.status === 'sent' || data.status === 'throttled') revealCode();
    } catch (error) {
      say('We could not reach the server. Please check your connection and try again.');
    } finally {
      if (sendButton) sendButton.disabled = false;
    }
  });

  codeForm.addEventListener('submit', async (event) => {
    event.preventDefault();

    const submit = codeForm.querySelector('button[type="submit"]');
    if (submit) submit.disabled = true;

    try {
      const data = await post(
        root.dataset.confirmUrl,
        'PATCH',
        {
          code: codeInput ? codeInput.value : '',
          next: codeForm.querySelector('input[name="next"]')?.value,
        },
      );

      if (!data) {
        say('We could not check that code just then. Please try again.');
        return;
      }

      if (data.status === 'verified') {
        window.location = data.redirect;
        return;
      }

      say(data.message);
      if (codeInput) codeInput.select();
    } catch (error) {
      say('We could not reach the server. Please check your connection and try again.');
    } finally {
      if (submit) submit.disabled = false;
    }
  });
}

function boot() {
  document.querySelectorAll('[data-proxy-verification]').forEach(initProxyVerification);
}

// Not DOMContentLoaded alone. The pack tag is emitted with `defer`, so on a
// warm cache this file can execute AFTER that event has already fired — and a
// listener registered then never runs, leaving the page with two plain forms
// and no explanation. Check the state of the document instead of assuming the
// event is still ahead of us.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

// And again on every subsequent visit, under whichever of the two this
// application is running. boot() is idempotent — initProxyVerification returns
// early on an element it has already wired.
document.addEventListener('turbo:load', boot);
document.addEventListener('turbolinks:load', boot);
